import React from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ColumnDef } from '@tanstack/react-table';

import { EditableTagsCell } from 'components/EditableTagsCell/EditableTagsCell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  UIButton
} from 'components/ui';

import { ProjectCategory } from '../constants';
import { ProjectItem } from '../types';
import {
  getProjectIcon,
  getProjectIconBgColor,
  getProjectTypeLabel
} from '../utils';

interface UseProjectsColumnsProps {
  onStarClick?: (item: ProjectItem) => void;
  starredItems?: Set<string>;
  onProjectClick?: (item: ProjectItem) => void;
  onUploadClick?: (item: ProjectItem) => void;
  onRemoveImage?: (item: ProjectItem) => void;
  onTagsUpdate?: (
    itemId: string,
    category: ProjectCategory,
    tags: string[]
  ) => void;
}

export const useProjectsColumns = ({
  onStarClick,
  starredItems,
  onProjectClick,
  onUploadClick,
  onRemoveImage,
  onTagsUpdate
}: UseProjectsColumnsProps): { columns: ColumnDef<ProjectItem>[] } => {
  const formatLastModified = (lastViewedAt?: number): string => {
    if (!lastViewedAt) return 'Never';

    const now = Date.now();
    const diffMs = now - lastViewedAt;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffHours < 24) {
      return diffHours <= 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else {
      return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
    }
  };

  const columns: ColumnDef<ProjectItem>[] = [
    {
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => {
        const item = row.original;
        const icon = getProjectIcon(
          item.type,
          item.subType,
          item.category,
          item.isParkingDomain
        );
        const iconBgColor = getProjectIconBgColor(
          item.type,
          item.subType,
          item.category,
          item.isParkingDomain
        );

        return (
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex-shrink-0">
              <div
                className={`w-8 h-8 ${iconBgColor} rounded-full flex items-center justify-center`}
              >
                <FontAwesomeIcon icon={icon} className="text-[10px]" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">
                {item.name}
              </div>
              {item.portalName && (
                <div className="text-xs text-gray-500 truncate">
                  Portal: {item.portalName}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const item = row.original;
        const typeLabel = getProjectTypeLabel(
          item.type,
          item.subType,
          item.category,
          item.isParkingDomain
        );
        return <span className="text-sm text-gray-900">{typeLabel}</span>;
      }
    },
    {
      accessorKey: 'tags',
      header: 'Tags',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <EditableTagsCell
            tags={item.tags || []}
            rowId={item.id}
            category={item.category}
            onTagsUpdate={onTagsUpdate}
          />
        );
      }
    },
    // {
    //     accessorKey: 'lastViewedAt',
    //     header: 'Last Modified',
    //     cell: ({ row }) => {
    //         const item = row.original;
    //         return (
    //             <span className="text-sm text-gray-500">
    //                 {formatLastModified(item.lastViewedAt)}
    //             </span>
    //         );
    //     }
    // },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const item = row.original;
        const isStarred = starredItems?.has(item.id) || false;

        return (
          <div className="flex items-center justify-end gap-2">
            {onStarClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStarClick(item);
                }}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
              >
                <FontAwesomeIcon
                  icon={isStarred ? 'fa-solid fa-star' : 'fa-regular fa-star'}
                  className={`text-sm ${isStarred ? 'text-yellow-400' : 'text-gray-400'}`}
                />
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <UIButton
                  variant="ghost"
                  size="sm"
                  className="w-8 h-8 p-0 hover:bg-gray-100 rounded transition-colors focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-ellipsis-vertical"
                    className="text-sm text-gray-400"
                  />
                </UIButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-max bg-white border border-[#E4E4E7] rounded-md shadow-lg"
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onUploadClick?.(item);
                  }}
                  className="cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-black whitespace-nowrap"
                >
                  Upload image thumbnail
                </DropdownMenuItem>
                {Boolean(item.thumbnailImageUrl) && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveImage?.(item);
                    }}
                    className="cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-black whitespace-nowrap"
                  >
                    Remove image thumbnail
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      }
    }
  ];

  return { columns };
};
